<template>
    <!--Page alert + loader-->
    <PageStatus :alert="defaultAlert" :table-data="tableData" />

        <!--Import Data-->
    <ImportModal
        :form-import-data="formImportData"
        :alert="defaultAlert"
        ask-source-system
        v-model:source-system="formImportData.source_system"
        @file-change="onFileImportChange"
        @import="importRecords"
        @download-template="downloadImportTemplate"
    >
        <!--Import Notes-->
        <div id="accordion">
            <div class="card">
                <div class="card-header">
                    <a class="btn" data-bs-toggle="collapse" href="#collapseOne">
                        Import Process
                    </a>
                </div>
                <div id="collapseOne" class="collapse show" data-bs-parent="#accordion">
                    <div class="card-body">
                        <ol>
                            <li>Click <b>Download Import Template</b>. It has the right columns and two sample rows. Columns marked <b>*</b> (red) are required.</li>
                            <li>Delete the sample rows and paste your companies, one per row. Keep row 1 (the column names). The order of the columns does not matter.</li>
                            <li><b>Old system ID</b>: the company's ID in your old system. Companies whose Old system ID was already imported are skipped.</li>
                            <li><b>Company name</b> must be new for your account. <b>Email</b> is required. <b>Website</b>, if given, must be a full address, e.g. https://example.com.</li>
                            <li>Each company gets a <b>Head Office</b> branch, a logo and the default brand colours, like one added by hand. You and the account owner get access to it.</li>
                            <li>Enter the <b>Old system name</b> (e.g. quickbooks). Use the same name for every file from that system, including when you import its branches and users.</li>
                            <li>For big imports with undo, use <b>Data Migration</b>.</li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>
        <!--End Import Notes-->
    </ImportModal>
    <!--Import Data-->

    <!--Filter Data Modal (shared: FilterModal.vue)-->
    <FilterModal
        title="Filter companies"
        :draft="filterDraft"
        v-model:search-by="tableData.searchBy"
        :view-options="viewOptions"
        :search-by-options="searchByOptions"
        :alert="defaultAlert"
        @search-by-change="changePlaceholder()"
        @apply="applyFilters"
        @clear="clearFilters"
    />
    <!--End Filter Data Modal-->

<!--Begin form-->
<RecordFormModal
    :title="isEditing ? 'Update Company Details' : 'Add Company Details'"
    title-icon="bi bi-building"
    :is-editing="isEditing"
    :is-saving="isSaving"
    :alert="defaultAlert"
    @save="saveOrEditRecord"
    @sample="addSampleData"
    @reset="resetForm"
>
    <!-- Hidden ID -->
    <HiddenField v-model="formInputs.id" />

    <!-- Row 1: Company Name & Registration Number -->
    <div class="row g-3 mb-0">
        <FormField
            v-model="formInputs.company_name"
            col="col-md-6"
            id="companyNameInput"
            label="Company Name"
            icon="fa fa-building"
            placeholder="Enter company name"
            tooltip="Please enter the company name"
            required
            @input="handleInputChange"
            @change="handleInputChange"
        />
        <FormField
            v-model="formInputs.registration_number"
            col="col-md-6"
            id="registrationNumberInput"
            label="Registration Number"
            icon="fa fa-id-card"
            placeholder="Enter registration number"
            tooltip="Enter the company registration number"
            @input="handleInputChange"
            @change="handleInputChange"
        />
    </div>

    <!-- Row 2: Tax Number & Email -->
    <div class="row g-3 mb-0">
        <FormField
            v-model="formInputs.tax_number"
            col="col-md-6"
            id="taxNumberInput"
            label="Tax Number"
            icon="fa fa-file-text-o"
            placeholder="Enter tax number"
            tooltip="Enter the company tax number"
            @input="handleInputChange"
            @change="handleInputChange"
        />
        <FormField
            v-model="formInputs.email"
            col="col-md-6"
            id="emailInput"
            type="email"
            label="Email"
            icon="fa fa-envelope"
            placeholder="Enter company email"
            tooltip="Enter a valid company email"
            @input="handleInputChange"
            @change="handleInputChange"
        />
    </div>

    <!-- Row 3: Phone & Website -->
    <div class="row g-3 mb-0">
        <FormField
            v-model="formInputs.phone"
            col="col-md-6"
            id="phoneInput"
            type="tel"
            label="Phone"
            icon="fa fa-phone"
            placeholder="Enter phone number"
            tooltip="Enter the company phone number"
            @input="handleInputChange"
            @change="handleInputChange"
        />
        <FormField
            v-model="formInputs.website"
            col="col-md-6"
            id="websiteInput"
            type="url"
            label="Website"
            icon="fa fa-globe"
            placeholder="https://example.com"
            tooltip="Enter the company website URL"
            @input="handleInputChange"
            @change="handleInputChange"
        />
    </div>

    <!-- Address Section -->
    <div class="card-header bg-sa-card-header d-flex justify-content-between align-items-center">
        <span><i class="bi bi-geo-alt me-2"></i>Address & Location</span>
    </div>

    <!-- Row 4: Address & City -->
    <div class="row g-3 mb-0">
        <FormField
            v-model="formInputs.address"
            col="col-md-6"
            id="addressInput"
            label="Address"
            icon="fa fa-home"
            placeholder="Enter address"
            tooltip="Enter the company physical address"
            @input="handleInputChange"
            @change="handleInputChange"
        />
        <FormField
            v-model="formInputs.city"
            col="col-md-6"
            id="cityInput"
            label="City"
            icon="fa fa-map-marker"
            placeholder="Enter city"
            tooltip="Enter the company city"
            @input="handleInputChange"
            @change="handleInputChange"
        />
    </div>

    <!-- Row 5: State & Country -->
    <div class="row g-3 mb-0">
        <FormField
            v-model="formInputs.state"
            col="col-md-6"
            id="stateInput"
            label="State / County"
            icon="fa fa-map"
            placeholder="Enter state or county"
            tooltip="Enter the state, county, or province"
            @input="handleInputChange"
            @change="handleInputChange"
        />
        <SelectField
            v-model="formInputs.country"
            col="col-md-6"
            id="countryInput"
            name="country"
            label="Country"
            icon="fa fa-globe"
            :options="countryOptions"
            :reduce="c => c.code"
            :get-option-label="c => `${$getFlag(c.code)} ${c.name}`"
            placeholder="Select country"
            tooltip="Please select the company country"
            required
            @change="onCountryChange"
        />
    </div>

    <!-- Row 6: Postal Code & Currency -->
    <div class="row g-3 mb-0">
        <FormField
            v-model="formInputs.postal_code"
            col="col-md-6"
            id="postalCodeInput"
            label="Postal Code"
            icon="fa fa-envelope-open"
            placeholder="Enter postal code"
            tooltip="Enter the company postal code"
            @input="handleInputChange"
            @change="handleInputChange"
        />
        <FormField
            v-model="formInputs.currency_code"
            col="col-md-6"
            id="currencyCodeInput"
            label="Currency"
            icon="fa fa-money"
            maxlength="3"
            pattern="[A-Za-z]{3}"
            class="text-uppercase"
            placeholder="e.g. KES"
            hint="Filled in from the country; you can change it"
            tooltip="A 3-letter currency code, e.g. KES"
            @input="handleInputChange"
            @change="handleInputChange"
        />
    </div>

    <!-- Company Settings Section -->
    <div class="card-header bg-sa-card-header d-flex justify-content-between align-items-center">
        <span><i class="bi bi-gear me-2"></i>Company Settings</span>
    </div>

    <!-- Row 7: Timezone & Active -->
    <div class="row g-3 mb-0">
        <SelectField
            v-model="formInputs.timezone"
            col="col-md-6"
            id="timezoneInput"
            name="timezone"
            label="Timezone"
            icon="fa fa-clock-o"
            :options="timezoneOptions"
            placeholder="e.g. Africa/Nairobi"
            tooltip="Type to search, e.g. Nairobi"
        />
        <CheckField
            v-model="formInputs.is_active"
            col="col-md-6"
            id="isActiveInput"
            label="Company Status"
            text="Active"
        />
    </div>

    <!-- Row 8: Logo & Current Location -->
    <div class="row g-3 mb-3">
        <FormField
            v-model="formInputs.file"
            col="col-md-6"
            id="logoInput"
            type="file"
            accept="image/*"
            data-max-size="5"
            label="Company Logo"
            icon="fa fa-image"
            :hint="isEditing ? 'Leave empty to keep the current logo' : ''"
            tooltip="An image, up to 5 MB"
            @change="handleInputChange"
        />
        <FormField
            v-model="formInputs.current_location"
            col="col-md-6"
            id="currentLocationInput"
            label="Current Location"
            icon="fa fa-location-arrow"
            placeholder="Enter current location"
            tooltip="Enter the current company location"
            @input="handleInputChange"
            @change="handleInputChange"
        />
    </div>

    <!-- Row 9: Brand & PDF style (opens its own modal) -->
    <div class="row g-3 mb-3">
        <div class="col-12">
            <label class="form-label mb-1">
                <i class="fa fa-paint-brush me-1"></i> Brand &amp; PDF Style
            </label>
            <div class="d-flex align-items-center flex-wrap gap-2 brand-summary">
                <span v-for="field in brandFields" :key="field.key"
                      class="brand-swatch"
                      :style="{ background: formInputs[field.key] }"
                      :title="field.label + ': ' + formInputs[field.key]"></span>

                <span class="badge bg-secondary">
                    <i class="fa fa-file-pdf-o me-1"></i>{{ brandTemplateLabel() }}
                </span>

                <button type="button"
                        class="btn btn-sm btn-outline-primary ms-auto"
                        data-bs-toggle="modal"
                        data-bs-target="#brandingModal">
                    <i class="fa fa-paint-brush me-1"></i> Choose colors
                </button>
            </div>
        </div>
    </div>
</RecordFormModal>
<!--End form-->

<!-- Brand & PDF Style Modal (opened from the company form) -->
<div class="modal fade" tabindex="-1" data-bs-focus="false" id="brandingModal">
    <div class="modal-dialog">
        <div class="modal-content bg-sa-modal-content">
            <div class="modal-body">
                <div class="card bg-sa-card">
                    <div class="card-header bg-sa-card-header d-flex justify-content-between align-items-center">
                        <span><i class="fa fa-paint-brush me-2"></i>Brand &amp; PDF Style
                            <small class="text-muted">— {{ formInputs.company_name || 'New company' }}</small>
                        </span>
                        <!-- ✕ goes back to the company form -->
                        <button type="button" class="btn-close color-sa-btn-close"
                                data-bs-toggle="modal" data-bs-target="#recordFormModal"></button>
                    </div>

                    <div class="card-body bg-sa-card-body p-2">
                        <div class="row g-3">

                            <!-- PDF Template -->
                            <SelectField
                                v-model="formInputs.brand_template"
                                col="col-12"
                                label="PDF Template"
                                icon="fa fa-file-pdf-o"
                                :options="brandTemplates"
                                option-label="label"
                                :reduce="t => t.value"
                                :clearable="false"
                                placeholder="Choose a template"
                            />

                            <!-- Colours -->
                            <div class="col-6" v-for="field in brandFields" :key="field.key">
                                <label :for="field.key + 'Input'" class="form-label small mb-1">{{ field.label }}</label>
                                <div class="input-group">
                                    <input v-model="formInputs[field.key]" type="color"
                                           class="form-control form-control-color brand-color-picker" :title="field.label" />
                                    <input v-model="formInputs[field.key]" @change="normalizeBrandColor(field.key)"
                                           type="text" :id="field.key + 'Input'" class="form-control text-uppercase"
                                           maxlength="7" placeholder="#000000" />
                                </div>
                                <small class="text-muted">{{ field.hint }}</small>
                            </div>

                            <!-- Live preview (changes with colours and template) -->
                            <div class="col-12">
                                <div class="brand-preview"
                                     :style="{ background: formInputs.brand_template === 'classic' ? formInputs.brand_background : formInputs.brand_paper,
                                               padding: formInputs.brand_template === 'classic' ? '12px' : '0' }">

                                    <div v-if="formInputs.brand_template === 'modern'" class="brand-preview-band"
                                         :style="{ background: formInputs.brand_primary, borderBottomColor: formInputs.brand_accent }">
                                        <span :style="{ color: brandBestOf(formInputs.brand_primary) }">{{ formInputs.company_name || 'Company Name' }}</span>
                                        <i :style="{ background: formInputs.brand_background }"></i>
                                    </div>

                                    <div v-if="formInputs.brand_template === 'minimal'" class="brand-preview-bar"
                                         :style="{ background: formInputs.brand_primary, borderBottomColor: formInputs.brand_accent }"></div>

                                    <div class="brand-preview-sheet"
                                         :style="{ background: formInputs.brand_paper,
                                                   borderColor: formInputs.brand_template === 'classic' ? formInputs.brand_accent : 'transparent' }">
                                        <div v-if="formInputs.brand_template !== 'modern'" class="brand-preview-title" :style="{ color: brandSheetTextColor() }">
                                            {{ formInputs.company_name || 'Company Name' }}
                                            <span :style="{ color: formInputs.brand_accent }">DATA EXPORT REPORT</span>
                                        </div>
                                        <div class="brand-preview-head" :style="{ background: formInputs.brand_primary, borderBottomColor: formInputs.brand_accent, color: brandHeadingTextColor() }">
                                            <span>#</span><span>NAME</span><span>EMAIL</span><span>DATE</span>
                                        </div>
                                        <div class="brand-preview-row" :style="{ background: brandRowColor(true) }"></div>
                                        <div class="brand-preview-row" :style="{ background: brandRowColor(false) }"></div>
                                        <div class="brand-preview-row" :style="{ background: brandRowColor(true) }"></div>
                                    </div>

                                    <div v-if="formInputs.brand_template === 'modern'" class="brand-preview-band bottom"
                                         :style="{ background: formInputs.brand_primary, borderTopColor: formInputs.brand_accent }"></div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            <div class="modal-footer bg-sa-modal-footer">
                <button type="button" class="btn btn-secondary me-2" @click="resetBrandColors">
                    <i class="fa fa-undo me-1"></i> Default
                </button>
                <!-- Done goes back to the company form (still unsaved until you click Save/Update there) -->
                <button type="button" class="btn btn-primary"
                        data-bs-toggle="modal" data-bs-target="#recordFormModal">
                    <i class="fa fa-check me-1"></i> Done
                </button>
            </div>
        </div>
    </div>
</div>
<!-- End Brand & PDF Style Modal -->

    <h4 class="sa-core-color fw-semibold mb-2">
        <i :class="[pageIcon, 'me-2 opacity-75']"></i>{{ pageTitle }}
    </h4>
    <!--Tables-->
    <div class="card glass mt-0 bg-sa-card">
        <div class="card-body bg-sa-card-body p-0">




            <!-- Table action row (shared: TableToolbar.vue) -->
            <TableToolbar
                can-add
                :filter-count="activeFilterCount"
                :columns="tableColumns"
                v-model:hidden-columns="hiddenColumns"
                :selected-count="tableData.selectedRecordIds.length"
                :deleted-view="tableData.recordFilter === 'deletedone'"
                :can-delete="$hasPermission('companies.delete')"
                @add="clearForm()"
                @filter="openFilters"
                @clear-selection="clearSelection"
                @bulk-action="runBulkAction"
            >
                <!-- More menu -->
                <template #more>
                            <li><h6 class="dropdown-header">Data</h6></li>
                            <li>
                                <button type="button" class="dropdown-item" data-bs-toggle="modal" data-bs-target="#importModal">
                                    <i class="fa fa-upload fa-fw me-1"></i> Import
                                </button>
                            </li>
                            <li>
                                <button type="button" class="dropdown-item" @click="exportRecords('xls')">
                                    <i class="fa fa-file-excel-o fa-fw me-1 text-success"></i> Export to Excel
                                </button>
                            </li>
                            <li>
                                <button type="button" class="dropdown-item" @click="exportRecords('csv')">
                                    <i class="fa fa-file-text-o fa-fw me-1 text-success"></i> Export to CSV
                                </button>
                            </li>
                            <li>
                                <button type="button" class="dropdown-item" @click="exportRecords('pdf')">
                                    <i class="fa fa-file-pdf-o fa-fw me-1 text-danger"></i> Export to PDF
                                </button>
                            </li>
                </template>
            </TableToolbar>
            <!-- Table Search Panel -->
            <SearchBox
                v-model:search-query="tableData.searchQuery"
                v-model:records-per-page="tableData.recordsPerPage"
                :placeholder="tableData.searchPlaceHolderText"
                @search-input="onSearchInput"
                @search-click="onSearchButtonClick"
                @per-page-change="onRecordsPerPageChange"
            />
            <!-- What the table is currently showing -->
            <ActiveFilters
                :chips="activeFilterChips"
                @remove="removeFilterChip"
                @clear="clearAllFilterChips"
            />
            <!-- Table -->
            <div class="table-responsive">
                
                <table
    class="table table-hover align-middle mb-0"
    id="bootstrapTable"
    tabindex="0"
    @keydown.up="$moveRowSelection(-1, $event)"
    @keydown.down="$moveRowSelection(1, $event)"
    @keydown.enter="$onTableEnter($event)"
>
        <thead>
        <tr>
            <th>
                <input type="checkbox" v-model="tableData.selectAll" @change="toggleSelectAll" />
            </th>
            <th class="hide-sm-td"><span class="visually-hidden">Details</span></th>
            <th class="button hide-sm-td">No</th>
            <SortableTh v-if="isColumnVisible('company_name')" column="company_name" :table-data="tableData" @sort="orderBy">Company</SortableTh>
            <SortableTh v-if="isColumnVisible('registration_number')" column="registration_number" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Registration No.</SortableTh>
            <SortableTh v-if="isColumnVisible('email')" column="email" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Email</SortableTh>
            <SortableTh v-if="isColumnVisible('phone')" column="phone" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Phone</SortableTh>
            <SortableTh v-if="isColumnVisible('country')" column="country" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Country</SortableTh>
            <SortableTh v-if="isColumnVisible('is_active')" column="is_active" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Status</SortableTh>
            <th class="button">Action</th>
        </tr>
    </thead>

    <tbody>
        <template v-for="(record, index) in tableData.records" :key="record.id">

            <!-- Main Row -->
            <tr class="clickable-row"
                :data-record-id="record.id"
                :class="{ 'active-row main-row': tableData.selectedRecordId === record.id, 'row-just-saved': isJustSaved(record) }"
                @click="$onRowClick(record, $event)"
                @dblclick="$onRowDblClick(record, $event)"
            >
                <td>
                    <input type="checkbox" v-model="tableData.selectedRecordIds" :value="record.id" />
                </td>
                <td class="expand-cell hide-sm-td"
                    :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                    @click="$toggleDetails(record.id)">
                <i class="fa fa-lg expand-icon ms-1"
                  :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                  :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
                </td>
                <td class="hide-sm-td">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}</td>
                <td v-if="isColumnVisible('company_name')">
                    <!-- Phones: + / − and the number go in front of the company (their columns are hidden) -->
                    <span
                        class="show-sm-inline expand-cell me-2"
                        :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                        @click="$toggleDetails(record.id)"
                    >
                        <i class="fa fa-lg expand-icon"
                           :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                           :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
                    </span>
                    <span class="show-sm-inline">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}. </span>
                    <img v-if="record.logo" :src="`${$baseUrl}/settings/${record.logo}`" height="24" class="me-1 rounded" />
                    {{ record.company_name }}
                </td>
                <td v-if="isColumnVisible('registration_number')" class="hide-sm-td">{{ record.registration_number }}</td>
                <td v-if="isColumnVisible('email')" class="hide-sm-td">{{ record.email }}</td>
                <td v-if="isColumnVisible('phone')" class="hide-sm-td">{{ record.phone }}</td>
                <td v-if="isColumnVisible('country')" class="hide-sm-td text-nowrap">
                    <span v-if="record.country" class="me-1">{{ $getFlag(record.country) }}</span>{{ $getCountryName(record.country) }}
                </td>
                <td v-if="isColumnVisible('is_active')" class="hide-sm-td">
                    <span :class="Number(record.is_active) === 1 ? 'badge bg-success' : 'badge bg-danger'">
                        {{ Number(record.is_active) === 1 ? 'Active' : 'Inactive' }}
                    </span>
                </td>
                <td>
                    <RowActions
                        :record="record"
                        :modal-id="'deleteCrudModal' + record.id"
                        :can-soft-delete="$hasPermission('companies.delete')
                            && Number(record.id) !== Number($store.state.company_id)"
                        :can-restore="$hasPermission('companies.delete')"
                        @edit="editRecord"
                        @delete-type="setDeleteType"
                    />

                    <!-- Delete Modal -->
                    <DeleteModal
                        :modal-id="'deleteCrudModal' + record.id"
                        :title="deleteModalTitle"
                        :message="deleteModalMessage"
                        @confirm="deleteRecord(record.id)"
                    />
                    <!-- End Delete Modal -->
                </td>
            </tr>

            <!-- Hidden expanded row -->
            <tr class="collapse-row">
                <td class="p-0"></td>
                <td class="p-0 hide-sm-td"></td>
                <td colspan="8" class="p-0">
                    <div :id="'tableDetails' + record.id" class="collapse table-collapse table-details">
                        <div class="p-3 details-list">
                            <div><i class="fa fa-hashtag"></i><strong>Company Number:</strong> {{ record.company_number }}</div>
                            <div><i class="fa fa-user"></i><strong>Creator Name:</strong> {{ [record.user_first_name, record.user_middle_name].filter(Boolean).join(' ') }}</div>
                            <div><i class="fa fa-building-o"></i><strong>Creator Company:</strong> {{ record.creator_company_name }}</div>
                            <div><i class="fa fa-sitemap"></i><strong>Creator Branch:</strong> {{ record.branch_name }}</div>

                            <!-- Shown on phones, or always when the column is hidden with the Columns button -->
                            <div :class="{ 'show-sm-td': isColumnVisible('company_name') }"><i class="fa fa-building"></i><strong>Company:</strong> {{ record.company_name }}</div>
                            <div :class="{ 'show-sm-td': isColumnVisible('registration_number') }"><i class="fa fa-id-card"></i><strong>Registration No.:</strong> {{ record.registration_number }}</div>
                            <div :class="{ 'show-sm-td': isColumnVisible('email') }"><i class="fa fa-envelope"></i><strong>Email:</strong> {{ record.email }}</div>
                            <div :class="{ 'show-sm-td': isColumnVisible('phone') }"><i class="fa fa-phone"></i><strong>Phone:</strong> {{ record.phone }}</div>
                            <div :class="{ 'show-sm-td': isColumnVisible('country') }"><i class="fa fa-globe"></i><strong>Country:</strong> <span v-if="record.country" class="me-1">{{ $getFlag(record.country) }}</span>{{ $getCountryName(record.country) }}</div>
                            <div :class="{ 'show-sm-td': isColumnVisible('is_active') }">
                                <i class="fa fa-toggle-on"></i><strong>Status:</strong>
                                <span :class="Number(record.is_active) === 1 ? 'badge bg-success' : 'badge bg-danger'">
                                    {{ Number(record.is_active) === 1 ? 'Active' : 'Inactive' }}
                                </span>
                            </div>

                            <!-- Always in details -->
                            <div><i class="fa fa-percent"></i><strong>Tax Number:</strong> {{ record.tax_number }}</div>
                            <div>
                                <i class="fa fa-link"></i><strong>Website:</strong>
                                <a v-if="record.website" :href="record.website" target="_blank" rel="noopener">{{ record.website }}</a>
                            </div>
                            <div><i class="fa fa-home"></i><strong>Address:</strong> {{ record.address }}</div>
                            <div><i class="fa fa-map-marker"></i><strong>City:</strong> {{ record.city }}</div>
                            <div><i class="fa fa-map"></i><strong>State / County:</strong> {{ record.state }}</div>
                            <div><i class="fa fa-envelope-open"></i><strong>Postal Code:</strong> {{ record.postal_code }}</div>
                            <div><i class="fa fa-money"></i><strong>Currency:</strong> {{ record.currency_code }}</div>
                            <div><i class="fa fa-clock-o"></i><strong>Timezone:</strong> {{ record.timezone }}</div>
                            <div v-if="$hasPermission('view-location')"><i class="fa fa-location-arrow"></i><strong>Created From:</strong> {{ record.current_location }}</div>

                            <div><i class="fa fa-edit"></i><strong>Updated By:</strong> {{ record.updatedby_firstname }}</div>
                            <div><i class="fa fa-calendar"></i><strong>Date Created:</strong> {{ $ConvertToLocalDate(record.date_created) }}</div>
                            <div><i class="fa fa-calendar"></i><strong>Date Updated:</strong> {{ $ConvertToLocalDate(record.date_updated) }}</div>
                            <div v-if="$hasPermission('view-ip-address')"><i class="fa fa-desktop"></i><strong>IP Address:</strong> {{ record.ip_address }}</div>
                        </div>
                    </div>
                </td>
            </tr>

        </template>

        <!-- No rows: blank while loading, then the empty message in the middle -->
        <tr v-if="tableData.records.length === 0" class="table-filler">
            <td colspan="10" class="text-center align-middle" :style="{ height: fullPageHeight() }">
                <TableEmptyState
                    v-if="!tableLoading"
                    :has-filters="activeFilterCount > 0 || !!tableData.appliedSearchQuery"
                    :can-add="true"
                    :title="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'No companies yet'"
                    :message="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'Click Add to create the first company.'"
                    icon="fa fa-building"
                    @add="clearForm()"
                    @clear="clearAllFilterChips"
                />
            </td>
        </tr>
    </tbody>

    <tfoot class="table-light">
        <tr>
            <td colspan="10">
                <!-- Pagination (hidden when there are no rows; its space is kept) -->
                <div v-if="tableData.records.length === 0 && tableData.currentPage <= 1" class="pagination-placeholder"></div>
                <Pagination
                    v-else
                    :currentPage="tableData.currentPage"
                    :totalPages="tableData.totalPages"
                    :hasNextPage="tableData.hasNextPage"
                    :totalRecords="tableData.totalRecords"
                    :startRecord="tableData.startRecord"
                    :endRecord="tableData.endRecord"
                    :showFullPagination="tableData.totalRecords > 0"
                    :lastKnownPage="lastKnownPage"
                    @page-changed="fetchRecords"
                    @loaderMessage="updateLoaderMessage"
                    @last-page-request="goToLastPage"
                />
            </td>
        </tr>
    </tfoot>
</table>
            </div>
            <!-- Bottom Controls: Delete & Pagination -->

        </div>
    </div>
    <!--End Tables-->
</template>
<script>

    import PageStatus from "../../../components/admin/PageStatus.vue";
    import TableToolbar from "../../../components/admin/TableToolbar.vue";
    import FilterModal from "../../../components/admin/FilterModal.vue";
    import ActiveFilters from "../../../components/admin/ActiveFilters.vue";
    import Pagination from "../../../components/admin/Pagination.vue";
    import TableEmptyState from "../../../components/admin/TableEmptyState.vue";
    import SortableTh from "../../../components/admin/SortableTh.vue";
    import DeleteModal from "../../../components/admin/DeleteModal.vue";
    import RowActions from "../../../components/admin/RowActions.vue";
    import SearchBox from "../../../components/admin/SearchBox.vue";
    import ImportModal from "../../../components/admin/ImportModal.vue";
    import RecordFormModal from "../../../components/admin/RecordFormModal.vue";
    import FormField from "../../../components/admin/FormField.vue";
    import SelectField from "../../../components/admin/SelectField.vue";
    import HiddenField from "../../../components/admin/HiddenField.vue";
    import CheckField from "../../../components/admin/CheckField.vue";

    import CompaniesMixin from "./mixins/CompaniesMixin.js";
    import tableMixin from "@/mixins/tableMixin.js";
    import axios from "axios";

    export default {
        name: "Companies",
        mixins: [tableMixin, CompaniesMixin], // shared table methods first, then Companies' own
        components: {
            PageStatus,
            TableToolbar,
            FilterModal,
            ActiveFilters,
            Pagination,
            TableEmptyState,
            SortableTh,
            DeleteModal,
            RowActions,
            SearchBox,
            ImportModal,
            RecordFormModal,
            FormField,
            SelectField,
            HiddenField,
            CheckField,
        },
        data() {
            return {
                // Standard data (isSaving, tableData, defaultAlert, formImportData ...) comes from tableMixin
                standardPageSetup: true,                  // tableMixin sets up Enter-to-save and table shortcuts
                apiUrl: "/settings/api_companies.php",    // used by tableMixin's fetchRecords, delete, import, export
                importTemplateFileName: "companies_import_template.xlsx",
                exportFileName: "companiesExport",        // name of the exported file
                columnsKey: "companies",                  // saves this page's hidden columns (Columns button)
                pageIcon: 'fa fa-building',               // same icon as this page in the sidebar
                lastAutoCurrency: "",                     // the currency the Country box filled in last
                formInputs: this.getDefaultFormInputs(),
                // Filter window: View
                viewOptions: [
                    { label: "All Companies", value: "all" },
                    { label: "Companies By Me", value: "byme" },
                    { label: "Deleted Companies", value: "deletedone" },
                ],
                // Filter window: Search By
                searchByOptions: [
                    { label: "Company Name", value: "company_name" },
                    { label: "Company Number", value: "company_number" },
                    { label: "Registration Number", value: "registration_number" },
                    { label: "Tax Number", value: "tax_number" },
                    { label: "Email", value: "email" },
                    { label: "Phone", value: "phone" },
                    { label: "Website", value: "website" },
                    { label: "Country", value: "country" },
                    { label: "City", value: "city" },
                    { label: "State / County", value: "state" },
                    { label: "Postal Code", value: "postal_code" },
                ],
                brandFields: [
                    { key: "brand_primary",    label: "Primary",    hint: "Headings & table header" },
                    { key: "brand_accent",     label: "Accent",     hint: "Frame & lines" },
                    { key: "brand_background", label: "Background", hint: "Page colour" },
                    { key: "brand_paper",      label: "Paper",      hint: "Inside sheet & rows" },
                ],
                // Same keys as pdfTemplates() in global_pdf_functions.php
                brandTemplates: [
                    { value: "classic", label: "Classic — framed sheet" },
                    { value: "modern",  label: "Modern — straight bands" },
                    { value: "minimal", label: "Minimal — plain, saves ink" },
                ],
            };
        },
        mounted() {
            this.openAddFromRoute();
        },
        async created() {
            //alert(this.$store.state.isLoggedIn);
            //this.retriveUserDetails();
            await this.fetchRecords(
                this.tableData.currentPage,
                this.tableData.currentSortColumn,
                "DESC",
                this.tableData.startDate,
                this.tableData.endDate,
                this.$loaderMessages.loaderMessageDefault,
            );
            this.$checkIfCanFetchLocation(); //Only applies if location is enabled for user. If not Log user out for him to enable location
        },
        computed: {
            // Countries for the Country box: [{ name: "Kenya", code: "KE", currency: "KES" }, ...] (globalFunctions.js)
            countryOptions() {
                return this.$getCountries();
            },
            // Every time zone the browser knows (Africa/Nairobi, Europe/London ...), plus the saved one if it's unusual
            timezoneOptions() {
                let list = [];
                try {
                    list = Intl.supportedValuesOf("timeZone");
                } catch (e) {
                    list = [Intl.DateTimeFormat().resolvedOptions().timeZone]; // older browsers: at least this one
                }
                const current = this.formInputs.timezone;
                return current && !list.includes(current) ? [current, ...list] : list;
            },
            // Page heading that follows the chosen filter.
            pageTitle() {
                const titles = {
                    all: "Companies",
                    byme: "Companies By Me",
                    deletedone: "Companies Deleted Records",
                };
                return titles[this.tableData.recordFilter] || "Companies";
            },
            // Columns the user can show or hide (Columns button)
            tableColumns() {
                return [
                    { key: "company_name", label: "Company" },
                    { key: "registration_number", label: "Registration No." },
                    { key: "email", label: "Email" },
                    { key: "phone", label: "Phone" },
                    { key: "country", label: "Country" },
                    { key: "is_active", label: "Status" },
                ];
            },
        },
        watch: {
            // TopBar "+ Add company" while already on this page
            "$route.query.add"(val) {
                if (val) this.openAddFromRoute();
            },
        },
        methods: {
            // Opens the Add form when the URL has ?add=1 (from the TopBar switcher)
            openAddFromRoute() {
                if (!this.$route.query.add) return;
                this.$nextTick(() => {
                    document.getElementById('addRecordBtn')?.click();
                });
                this.$router.replace({ query: {} });
            },
            // Selection bar: the company you are logged into is never deleted (same rule as the row menu).
            // Take it out of the ticked rows, then run the shared bulk action.
            runBulkAction(deleteType = "soft") {
                if (deleteType === "soft") {
                    const currentCompanyId = String(this.$store.state.company_id);
                    const kept = this.tableData.selectedRecordIds.filter((id) => String(id) !== currentCompanyId);
                    if (!kept.length) {
                        this.handleAlert("ending", "The company you are logged into can't be deleted.", "warning", "page");
                        return;
                    }
                    this.tableData.selectedRecordIds = kept;
                }
                return tableMixin.methods.runBulkAction.call(this, deleteType);
            },
            getDefaultFormInputs() {
                return {
                    // Form Data
                    id: "",
                    company_number: "",
                    legacy_source: "",
                    legacy_id: "",

                    owner_user_id: "",
                    company_id: "",
                    branch_id: "",

                    company_name: "",
                    registration_number: "",
                    tax_number: "",
                    email: "",
                    phone: "",
                    website: "",

                    current_location: "",

                    address: "",
                    city: "",
                    state: "",
                    country: "",
                    postal_code: "",

                    currency_code: "",
                    timezone: "",

                    // Brand colours (PDF reports)
                    ...this.brandDefaults(),

                    logo: "",
                    is_active: true,

                    created_by: "",
                    updated_by: "",

                    ip_address: "",
                    is_deleted: 0,

                    date_created: "",
                    date_updated: "",
                    date_deleted: "",

                    // File Data
                    crudImage: "",
                    file: null,
                };
            },
            getDefaultFormImportData() {
                return {
                    dataFile: null,
                    source_system: "",      // old system name, e.g. quickbooks
                    templateLoading: false, // spinner on the Download Import Template button
                    importErrors: [],       // rows that were not imported, and why
                    uploading: false,
                    progress: 0,
                    message: "",
                    messageType: "",
                };
            },
            // Table defaults: the standard ones (tableMixin) + this page's own
            getDefaultTableData() {
                return {
                    ...this.baseTableData(),
                    searchBy: "company_name",
                    searchPlaceHolderText: "Search by Company Name",
                };
            },
            addSampleData() {
                this.formInputs = {
                    ...this.getDefaultFormInputs(),

                    id: "",

                    company_number: "COMP-001",
                    legacy_source: "",
                    legacy_id: "",

                    owner_user_id: "",
                    company_id: "",
                    branch_id: "",

                    company_name: "wihfocus Media LTD",
                    registration_number: "CPR-001234",
                    tax_number: "P051234567A",
                    email: "info@wihfocus.com",
                    phone: "0711930184",
                    website: "https://www.wihfocus.com",

                    current_location: "Nairobi",

                    address: "P.O. Box 12345",
                    city: "Nairobi",
                    state: "Nairobi County",
                    country: "KE",
                    postal_code: "00100",

                    currency_code: "KES",
                    timezone: "Africa/Nairobi",

                    logo: "",
                    is_active: true,

                    created_by: "",
                    updated_by: "",

                    ip_address: "",
                    is_deleted: 0,

                    date_created: "",
                    date_updated: "",
                    date_deleted: "",

                    file: null,
                };

                this.submitted = false;

                this.$clearValidationStates("#recordForm");

                this.$scrollToTopModal("recordFormModal");
            },
            saveOrEditRecord() {
                if (this.isSaving) return;
                this.isSaving = true;

                try {
                this.$scrollToTopModal("recordFormModal");
                this.submitted = true;

                const formIsValid = this.$validateForm("#recordForm");
                if (!formIsValid) {
                    this.$playErrorSound();
                        this.handleAlert("starting", "Please correct the highlighted fields.", "danger", "modal");
                    this.isSaving = false;
                    return;
                }
                //if (!this.$handleFormValidation('#recordForm')) { return; }
                this.handleAlert("starting", "Please wait submiting", "info", "modal");
                let formData = this.formData_fields();
                if (!this.formInputs.id) {
                    this.tableData.currentPage = 1;
                }
                
                axios
                    .post(this.apiUrl, formData, this.$handleConfig())
                    .then((response) => {
                        if (response.data.status == "success") {
                            this.handleSaveOrEditSuccess(response);
                        } else {
                            this.handleError("starting", response.data.message[0], "danger", "modal");
                        }
                    })
                    .catch((error) => {
                        this.handleRequestError(error, "modal");
                    })
                    .finally(() => {
                        this.isSaving = false;
                    });} catch (error) {
                    this.isSaving = false;
                    throw error;
                }
            },
            formData_fields() {
                let formData = new FormData();                
                formData.append("id", this.formInputs.id);

                formData.append("company_number", this.formInputs.company_number);
                formData.append("legacy_source", this.formInputs.legacy_source);
                formData.append("legacy_id", this.formInputs.legacy_id);

                formData.append("company_name", this.formInputs.company_name);
                formData.append("registration_number", this.formInputs.registration_number);
                formData.append("tax_number", this.formInputs.tax_number);
                formData.append("email", this.formInputs.email);
                formData.append("phone", this.formInputs.phone);
                formData.append("website", this.formInputs.website);

                formData.append("latitude", this.$store.state.latitude);
                formData.append("longitude", this.$store.state.longitude);

                formData.append("address", this.formInputs.address);
                formData.append("city", this.formInputs.city);
                formData.append("state", this.formInputs.state);
                formData.append("country", this.formInputs.country);
                formData.append("postal_code", this.formInputs.postal_code);
                formData.append("currency_code", this.formInputs.currency_code);
                formData.append("timezone", this.formInputs.timezone);
                formData.append("brand_primary", this.formInputs.brand_primary);
                formData.append("brand_accent", this.formInputs.brand_accent);
                formData.append("brand_background", this.formInputs.brand_background);
                formData.append("brand_paper", this.formInputs.brand_paper);
                formData.append("brand_template", this.formInputs.brand_template);

                formData.append("is_active", this.formInputs.is_active);

                if (this.formInputs.file) {
                    formData.append("file", this.formInputs.file);
                }

                formData.append("action", "saveOrEditRecord");

                return formData;
            },
            // "KE", "ke" or "Kenya" -> "KE" ("" if not found, so the box asks for it)
            countryCode(value) {
                const text = String(value ?? "").trim().toLowerCase();
                if (!text) return "";
                const found = this.$getCountries().find((c) => c.code.toLowerCase() === text || c.name.toLowerCase() === text);
                return found ? found.code : "";
            },
            // Country picked: fill in its currency (unless you typed your own)
            onCountryChange(code) {
                this.$clearValidationAlert("#recordForm");
                const country = this.$getCountries().find((c) => c.code === code);
                if (!country) return;
                if (!this.formInputs.currency_code || this.formInputs.currency_code === this.lastAutoCurrency) {
                    this.formInputs.currency_code = country.currency;
                }
                this.lastAutoCurrency = country.currency;
            },
            editRecord(records) {
                this.isEditing = true;

                this.formInputs.id = records.id ?? "";

                this.formInputs.company_number = records.company_number ?? "";
                this.formInputs.legacy_source = records.legacy_source ?? "";
                this.formInputs.legacy_id = records.legacy_id ?? "";

                this.formInputs.owner_user_id = records.owner_user_id ?? "";
                this.formInputs.company_id = records.company_id ?? "";
                this.formInputs.branch_id = records.branch_id ?? "";

                this.formInputs.company_name = records.company_name ?? "";
                this.formInputs.registration_number = records.registration_number ?? "";
                this.formInputs.tax_number = records.tax_number ?? "";
                this.formInputs.email = records.email ?? "";
                this.formInputs.phone = records.phone ?? "";
                this.formInputs.website = records.website ?? "";

                this.formInputs.current_location = records.current_location ?? "";

                this.formInputs.address = records.address ?? "";
                this.formInputs.city = records.city ?? "";
                this.formInputs.state = records.state ?? "";
                this.formInputs.country = this.countryCode(records.country);
                this.formInputs.postal_code = records.postal_code ?? "";
                this.formInputs.currency_code = records.currency_code ?? "";
                this.formInputs.timezone = records.timezone ?? "";

                const brand = this.brandDefaults();
                this.formInputs.brand_primary = records.brand_primary || brand.brand_primary;
                this.formInputs.brand_accent = records.brand_accent || brand.brand_accent;
                this.formInputs.brand_background = records.brand_background || brand.brand_background;
                this.formInputs.brand_paper = records.brand_paper || brand.brand_paper;
                this.formInputs.brand_template = records.brand_template || brand.brand_template;

                this.formInputs.logo = records.logo ?? "";
                this.formInputs.is_active = Number(records.is_active) === 1;

                this.formInputs.file = null;

                this.$clearValidationStates("#recordForm");
            },

                        
        },
    };
</script>
